import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { Chart, ChartData, ChartOptions } from 'chart.js/auto';
import { ExternalTooltip } from '../domain/externalTooltip';
import { Day, Days, Hour } from '../domain/day';

@Component({
  selector: 'ps-ui-line-chart',
  templateUrl: './line-chart.component.html',
  styleUrls: ['./line-chart.component.css'],
})
export class LineChartComponent implements OnInit, AfterViewInit {
  public chart: Chart;

  //Default paremeters to keep the same styling
  private readonly _fill = false;
  private readonly _tension = 0;

  private _dataSets: Array<CompleteDataSet> = [];
  private chartSize = 0;

  @Input()
  public elementId!: string;
  days: Days;

  @Input()
  set dataSets(days: Array<Day>) {
    this.days = new Days(days);
    const extractAllHours: Array<Hour> = this.days.getAllHours();

    /////// Make the size of the Chart dinamyc ////////////////
    this.chartSize =
      this.chartSize > extractAllHours.length
        ? this.chartSize
        : extractAllHours.length;
    /////// ////////////////////////////////////////// ////////////////

    const lineDataSets = [
      {
        label: 'actual',
        data: extractAllHours
          .filter((hour) => !hour.predict)
          .map((hour) => hour.actual),
        borderColor: 'rgba(91,141,189, 1)',
        fill: this._fill,
        tension: this._tension,
      },
      {
        label: 'expected',
        data: extractAllHours
          .filter((hour) => !hour.predict)
          .map((hour) => hour.expected),
        borderColor: 'rgba(199,204,208, 1)',
        fill: this._fill,
        tension: this._tension,
      },
      {
        label: 'expectedProjection',
        data: extractAllHours
          .filter((hour) => !hour.predict)
          .map((hour) => null)
          .concat(
            extractAllHours
              .filter((hour) => hour.predict)
              .map((hour) => hour.expected) as any
          ),
        borderColor: 'rgba(116,198,232, 1)',
        borderDash: [3, 3],
        fill: this._fill,
        tension: this._tension,
      },
    ];
    this._dataSets = lineDataSets;
  }

  @Output() getDay = new EventEmitter<Day>();

  externalTooltip = (context: any) => {
    new ExternalTooltip().render(context, this.days, 'line');
  };

  private _options = {
    elements: {
      point: {
        radius: 0.5,
      },
    },
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
        external: this.externalTooltip,
      },
    },
    layout: {
      padding: {
        bottom: 0,
        top: 0,
      },
    },
    interaction: {
      intersect: false,
    },
    scales: {
      x: {
        display: false,
        grid: {
          display: false,
        },
      },
      y: {
        display: false,
        grid: {
          display: false,
        },
      },
    },
    onClick: (e: any) => {
      const tooltipModel = e.chart.tooltip;
      const titleLines = tooltipModel.title || -1;
      const day = this.days.getDay(titleLines);
      this.getDay.emit(day);
    },
  };

  public ngOnInit(): void {
    if (!this.elementId) {
      throw new Error('Element id must be provided');
    }
  }

  public ngAfterViewInit() {
    this._build();
  }

  private _build() {
    const data = {
      labels: Array.from({ length: this.chartSize }, (x, i) => i),
      datasets: this._dataSets,
    };
    this.chart = new Chart(this.elementId, {
      type: 'line',
      data: <ChartData>data,
      options: <ChartOptions>this._options,
    });
  }
}

type CompleteDataSet = {
  fill: boolean;
  tension: number;
  data: Array<null | number>;
};
